import { useCallback, useEffect, useRef, useState } from 'react';

// Generic infinite-scroll loader for endpoints that return:
//   { items, total, summary, columns, has_more }
//
// Usage:
//   const { items, summary, columns, total, loading, loadingMore,
//           hasMore, error, sentinelRef, updateItem, reset } =
//     useInfiniteScroll(fetchPage, [fetchPage], { pageSize: 100 });
//
// ``fetchPage(offset, limit)`` returns a Promise resolving to the response.
// Pass anything in ``deps`` that should trigger a reset (filters, search,
// sort). The ``versionRef`` guard discards stale responses if the user
// changes filters mid-flight.
//
// ``rootRef`` is the scrolling element the sentinel lives in (a
// ``.table-scroll`` wrapper). Tables that scroll inside their own box need it:
// against the default viewport root the sentinel is clipped by the wrapper, so
// reaching the bottom of the table would not page in the next batch until the
// window itself was scrolled far enough to reveal the wrapper's bottom edge.
export function useInfiniteScroll(fetchFn, deps, { pageSize = 100, rootRef = null } = {}) {
  const [items, setItems]           = useState([]);
  const [summary, setSummary]       = useState(null);
  const [columns, setColumns]       = useState([]);
  const [total, setTotal]           = useState(0);
  const [loading, setLoading]       = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore]       = useState(false);
  const [error, setError]           = useState(null);

  const offsetRef    = useRef(0);
  const sentinelRef  = useRef(null);
  const fetchingRef  = useRef(false);
  const versionRef   = useRef(0);
  const fetchFnRef   = useRef(fetchFn);
  fetchFnRef.current = fetchFn;

  const reset = useCallback(async () => {
    const version = ++versionRef.current;
    fetchingRef.current = true;
    setLoading(true);
    setError(null);
    offsetRef.current = 0;
    try {
      const res = await fetchFnRef.current(0, pageSize);
      if (versionRef.current !== version) return;
      setItems(res.items || []);
      setSummary(res.summary || null);
      setColumns(res.columns || []);
      setTotal(res.total || 0);
      setHasMore(res.has_more || false);
      offsetRef.current = (res.items || []).length;
    } catch (e) {
      if (versionRef.current !== version) return;
      setError(e.message);
      setItems([]);
    }
    setLoading(false);
    fetchingRef.current = false;
  }, [pageSize]);

  const loadMore = useCallback(async () => {
    if (fetchingRef.current || !hasMore) return;
    const version = versionRef.current;
    fetchingRef.current = true;
    setLoadingMore(true);
    try {
      const res = await fetchFnRef.current(offsetRef.current, pageSize);
      if (versionRef.current !== version) { fetchingRef.current = false; return; }
      const newItems = res.items || [];
      setItems((prev) => [...prev, ...newItems]);
      setHasMore(res.has_more || false);
      offsetRef.current += newItems.length;
    } catch (e) {
      if (versionRef.current !== version) { fetchingRef.current = false; return; }
      setError(e.message);
    }
    setLoadingMore(false);
    fetchingRef.current = false;
  }, [hasMore, pageSize]);

  useEffect(() => { reset(); }, deps); // eslint-disable-line react-hooks/exhaustive-deps

  // Two paging triggers, one per layout — ``loading`` is in both dep lists so
  // they re-attach to the freshly mounted nodes after a tab/filter change
  // unmounts the previous ones.
  //
  // A table that scrolls inside its own box watches that box's scroll offset
  // directly rather than observing the sentinel. Chrome clips an
  // element-rooted IntersectionObserver's bounds to the viewport, and the box
  // is taller than the room left below the summary cards, so its bottom edge —
  // where the sentinel sits — starts out below the window: whether the
  // sentinel can ever report visible would depend on the WINDOW's scroll
  // position as much as the box's. Reading scrollTop drops that coupling.
  useEffect(() => {
    const root = rootRef?.current;
    if (!root) return;
    const check = () => {
      if (!hasMore || fetchingRef.current) return;
      // Rules keeps every explorer mounted under `display: none` so tab
      // switches are instant. Those boxes measure 0×0, which reads as "the
      // content does not fill the box" and would page the WHOLE table in on
      // mount, for every hidden tab at once.
      if (root.clientHeight === 0) return;
      if (root.scrollHeight - root.scrollTop - root.clientHeight < 300) loadMore();
    };
    root.addEventListener('scroll', check, { passive: true });
    // Fires on attach and again when a hidden tab is shown (0 → real height),
    // covering the case where one page does not fill the box.
    const resize = new ResizeObserver(check);
    resize.observe(root);
    return () => {
      root.removeEventListener('scroll', check);
      resize.disconnect();
    };
  }, [hasMore, loadMore, loading, items.length, rootRef]);

  // Page-scrolled tables (no scroll box) keep the sentinel observer.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || rootRef?.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !fetchingRef.current) loadMore();
      },
      { rootMargin: '200px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadMore, loading, rootRef]);

  const updateItem = useCallback((idKey, idVal, updates) => {
    setItems((prev) => prev.map((r) => (r[idKey] === idVal ? { ...r, ...updates } : r)));
  }, []);

  const removeItems = useCallback((idKey, idVals) => {
    const idSet = new Set(idVals);
    setItems((prev) => {
      const next = prev.filter((r) => !idSet.has(r[idKey]));
      const removed = prev.length - next.length;
      if (removed > 0) {
        setTotal((t) => Math.max(0, t - removed));
        offsetRef.current = Math.max(0, offsetRef.current - removed);
      }
      return next;
    });
  }, []);

  return {
    items, summary, columns, total,
    loading, loadingMore, hasMore, error,
    sentinelRef, updateItem, removeItems, reset,
  };
}
